import { useMutation, useQuery } from '@apollo/client';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { format, parseISO } from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { ForecastTable } from 'src/components/Finance/ForecastTable';
import { attachCompareToFinanceRows } from 'src/components/Finance/financeCompare';
import type { FinanceCompareMode } from 'src/components/Finance/financeCompare';
import {
  categoryDisplayLabel,
} from 'src/components/Finance/costRuleInference';
import {
  CustomCostsPanel,
  emptyFinanceCostSettingsForm,
  financeCostSettingsToForm,
  parseFinanceCostSettingsForm,
  type FinanceCostSettingsForm,
} from 'src/components/Finance/CustomCostsPanel';
import { downloadFinanceCsv } from 'src/components/Finance/exportFinanceCsv';
import { downloadFinancePdf } from 'src/components/Finance/exportFinancePdf';
import { granularityLabel } from 'src/components/Finance/formatters';
import { SectionCard } from 'src/components/Retention/SectionCard';
import { useGoogleAnalytics } from 'src/hooks/useGoogleAnalytics';
import { useTrialStatus } from 'src/hooks/useTrialStatus';
import { useSnackbar } from 'src/providers/snackbar/hook/useSnackbar';
import { useSidebar } from 'src/providers/sidebar/useSidebar';
import palette from 'src/theme/palette';
import { KIPIFY_FILTER_APPLY_BUTTON_CLASS } from 'src/theme/kipifyUi';
import {
  getLastNDaysDateRange,
  getLastNMonthsDateRange,
  getPreviousPeriodDateRange,
  getPreviousYearDateRange,
  getThisMonthDateRange,
  getThisYearDateRange,
} from 'src/utils/dateUtils';
import {
  GET_FINANCE_COST_SETTINGS,
  GET_FINANCE_HISTORICAL,
  UPDATE_FINANCE_COST_SETTINGS,
} from 'src/utils/gql/financeQueries';
import {
  getLookbackCompareAutoDisabledMessage,
  getLookbackCompareBlockedMessage,
  getLookbackCustomRangeBlockedMessage,
  getLookbackPlanLimitCaption,
  getLookbackPresetBlockedMessage,
  getLookbackThisMonthBlockedMessage,
  getLookbackThisYearBlockedMessage,
} from 'src/utils/lookbackMessages';

const PageContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  minHeight: '100vh',
  width: '100%',
  boxSizing: 'border-box',
  overflow: 'visible',
  [theme.breakpoints.up('sm')]: {
    marginLeft: '240px',
    width: 'calc(100% - 240px)',
    maxWidth: 'calc(100% - 240px)',
  },
  [theme.breakpoints.down('md')]: {
    marginLeft: '220px',
    width: 'calc(100% - 220px)',
    maxWidth: 'calc(100% - 220px)',
  },
  [theme.breakpoints.down('sm')]: {
    marginLeft: 0,
    width: '100%',
    marginTop: '60px',
    maxWidth: '100%',
  },
}));

const ContentContainer = styled(Box)(() => ({
  width: '100%',
  maxWidth: '100%',
  margin: 0,
  padding: '20px 20px 28px',
  boxSizing: 'border-box',
  minHeight: '100%',
}));

const textPrimary = palette.text.primary;
const textSecondary = palette.text.secondary;

const periodRowControlHeight = 40;
const periodRowControlSx = {
  height: periodRowControlHeight,
  minHeight: periodRowControlHeight,
  borderRadius: 2,
  backgroundColor: 'white',
  '& .MuiOutlinedInput-root': {
    height: periodRowControlHeight,
    minHeight: periodRowControlHeight,
    borderRadius: 2,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(15, 23, 42, 0.14)',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(15, 23, 42, 0.22)',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: palette.primary.main,
    borderWidth: 1,
  },
  '& .MuiSelect-icon': {
    color: textSecondary,
  },
} as const;

const periodRowLabelSx = {
  display: 'block',
  mb: 0.5,
  fontSize: '0.75rem',
  fontWeight: 600,
  color: textSecondary,
  letterSpacing: '0.01em',
} as const;

type FinanceCostSettingsQueryData = {
  getFinanceCostSettings: {
    rules: Array<{
      id: string;
      label: string;
      category: string;
      amount: number;
      frequency: string;
    }>;
  };
};

type FinanceHistoricalQueryData = {
  getFinanceHistorical: {
    periods: string[];
    granularity: string;
    dateRange: { from: string; to: string };
    warnings: string[];
    noData?: boolean | null;
    error?: string | null;
    appliedCostSettings?: {
      rules: Array<{
        id: string;
        label: string;
        category: string;
        amount: number;
        frequency: string;
      }>;
    } | null;
    rows: Array<{
      metricKey: string;
      label: string;
      format: string;
      emphasis: string;
      values: number[];
    }>;
  };
};

function formatSavedCostSummary(settings?: {
  rules?: Array<{
    label: string;
    category: string;
    amount: number;
    frequency: string;
  }>;
} | null): string | null {
  if (!settings?.rules?.length) return null;
  return settings.rules
    .map((rule) => {
      const amount = `€${rule.amount.toLocaleString('en-GB')}`;
      const cadence = rule.frequency === 'per_order' ? '/order' : '/month';
      return `${rule.label} (${categoryDisplayLabel(rule.category as 'marketing' | 'operating' | 'per_order')}) ${amount}${cadence}`;
    })
    .join(' · ');
}

function settingsEqual(
  a: ReturnType<typeof parseFinanceCostSettingsForm>,
  b: ReturnType<typeof parseFinanceCostSettingsForm>,
): boolean {
  if (a.rules.length !== b.rules.length) return false;
  return a.rules.every((rule, index) => {
    const other = b.rules[index];
    if (!other) return false;
    return (
      rule.id === other.id &&
      rule.label === other.label &&
      rule.category === other.category &&
      rule.amount === other.amount &&
      rule.frequency === other.frequency
    );
  });
}

function formatRangeLabel(from: string, to: string): string {
  try {
    const start = parseISO(from.length <= 10 ? `${from}T12:00:00` : from);
    const end = parseISO(to.length <= 10 ? `${to}T12:00:00` : to);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return `${from} – ${to}`;
    }
    return `${format(start, 'MMM d, yyyy')} – ${format(end, 'MMM d, yyyy')}`;
  } catch {
    return `${from} – ${to}`;
  }
}

const FinancePage = () => {
  const theme = useTheme();
  const snackbar = useSnackbar();
  const { collapsed: sidebarCollapsed } = useSidebar();
  const { lookbackDays } = useTrialStatus();
  const [searchParams] = useSearchParams();
  const { trackCustomEvent } = useGoogleAnalytics();
  const containerSx = sidebarCollapsed
    ? {
        [theme.breakpoints.up('sm')]: {
          marginLeft: '72px',
          width: 'calc(100% - 72px)',
          maxWidth: 'calc(100% - 72px)',
        },
      }
    : undefined;

  const [dateRangePreset, setDateRangePreset] = useState<string>('180');
  // 180/365 = mesi di calendario (non giorni rolling), così Meta/Google allineano ai mesi interi
  const [dateRange, setDateRange] = useState(() => getLastNMonthsDateRange(6));
  const [emailDeepLinkApplied, setEmailDeepLinkApplied] = useState(false);
  const [showCustomDateRange, setShowCustomDateRange] = useState(false);
  const [tempCustomDates, setTempCustomDates] = useState<{
    from: string;
    to: string;
  } | null>(null);
  const [compareMode, setCompareMode] = useState<FinanceCompareMode>('none');
  const [compareDateRange, setCompareDateRange] = useState<{
    from: string;
    to: string;
  } | null>(null);
  const [showCustomCompareDates, setShowCustomCompareDates] = useState(false);
  const [tempCustomCompareDates, setTempCustomCompareDates] = useState<{
    from: string;
    to: string;
  } | null>(null);
  const [costPanelOpen, setCostPanelOpen] = useState(true);
  const [costForm, setCostForm] = useState<FinanceCostSettingsForm>(
    emptyFinanceCostSettingsForm,
  );
  const [savedCostForm, setSavedCostForm] = useState<FinanceCostSettingsForm>(
    emptyFinanceCostSettingsForm,
  );
  const [exportingPdf, setExportingPdf] = useState(false);

  const parsedCostForm = useMemo(() => parseFinanceCostSettingsForm(costForm), [costForm]);
  const parsedSavedCostForm = useMemo(
    () => parseFinanceCostSettingsForm(savedCostForm),
    [savedCostForm],
  );
  const costFormDirty = !settingsEqual(parsedCostForm, parsedSavedCostForm);
  const activeCostSettings = useMemo(
    () => (costFormDirty ? parsedCostForm : parsedSavedCostForm),
    [costFormDirty, parsedCostForm, parsedSavedCostForm],
  );

  const { data: costSettingsData } = useQuery<FinanceCostSettingsQueryData>(
    GET_FINANCE_COST_SETTINGS,
    { fetchPolicy: 'network-only', errorPolicy: 'all' },
  );

  useEffect(() => {
    const next = financeCostSettingsToForm(costSettingsData?.getFinanceCostSettings);
    setCostForm(next);
    setSavedCostForm(next);
  }, [costSettingsData]);

  // Deep-link from email recap CTA (?from=&to=&utm_campaign=daily|weekly)
  useEffect(() => {
    if (emailDeepLinkApplied) return;
    const from = searchParams.get('from');
    const to = searchParams.get('to');
    const iso = /^\d{4}-\d{2}-\d{2}$/;
    if (!from || !to || !iso.test(from) || !iso.test(to) || from > to) return;

    setDateRange({ from, to });
    setDateRangePreset('custom');
    setEmailDeepLinkApplied(true);

    if (searchParams.get('utm_medium') === 'recap') {
      trackCustomEvent('recap_cta_clicked', {
        recap_type: searchParams.get('utm_campaign') || undefined,
        date_from: from,
        date_to: to,
      });
    }
  }, [searchParams, emailDeepLinkApplied, trackCustomEvent]);

  const [updateCostSettings, { loading: savingCostSettings }] = useMutation(
    UPDATE_FINANCE_COST_SETTINGS,
    {
      onCompleted: (result) => {
        const next = financeCostSettingsToForm(result.updateFinanceCostSettings);
        setCostForm(next);
        setSavedCostForm(next);
        snackbar('Additional costs saved', 'success');
      },
      onError: (apolloError) => {
        const message =
          apolloError.graphQLErrors[0]?.message ||
          apolloError.message ||
          'Could not save additional costs';
        snackbar(message, 'error');
      },
    },
  );

  const lookbackMinDate = useMemo(() => {
    if (lookbackDays === null || lookbackDays === undefined) return null;
    const min = new Date();
    min.setDate(min.getDate() - lookbackDays);
    return min.toISOString().split('T')[0];
  }, [lookbackDays]);

  const canUsePresetDays = useCallback(
    (days: number) => {
      if (lookbackDays === null || lookbackDays === undefined) return true;
      return days <= lookbackDays;
    },
    [lookbackDays],
  );

  const isRangeWithinLookback = useCallback(
    (range: { from: string; to: string } | null | undefined) => {
      if (!range || !lookbackMinDate) return true;
      return range.from >= lookbackMinDate;
    },
    [lookbackMinDate],
  );

  const canUseThisMonthPreset = useMemo(() => {
    if (!lookbackMinDate) return true;
    const thisMonthRange = getThisMonthDateRange();
    return thisMonthRange.from >= lookbackMinDate;
  }, [lookbackMinDate]);

  const canUseThisYearPreset = useMemo(() => {
    if (!lookbackMinDate) return true;
    const thisYearRange = getThisYearDateRange();
    return thisYearRange.from >= lookbackMinDate;
  }, [lookbackMinDate]);

  const previousPeriodRangeCandidate = useMemo(
    () => getPreviousPeriodDateRange(dateRange),
    [dateRange],
  );
  const previousYearRangeCandidate = useMemo(
    () => getPreviousYearDateRange(dateRange),
    [dateRange],
  );
  const canUsePreviousPeriodCompare = useMemo(
    () => isRangeWithinLookback(previousPeriodRangeCandidate),
    [isRangeWithinLookback, previousPeriodRangeCandidate],
  );
  const canUsePreviousYearCompare = useMemo(
    () => isRangeWithinLookback(previousYearRangeCandidate),
    [isRangeWithinLookback, previousYearRangeCandidate],
  );

  useEffect(() => {
    if (compareMode === 'previous_period') {
      setCompareDateRange(getPreviousPeriodDateRange(dateRange));
    } else if (compareMode === 'previous_year') {
      setCompareDateRange(getPreviousYearDateRange(dateRange));
    }
  }, [dateRange, compareMode]);

  useEffect(() => {
    if (compareMode === 'previous_period' && !canUsePreviousPeriodCompare) {
      setCompareMode('none');
      setCompareDateRange(null);
      snackbar(
        getLookbackCompareAutoDisabledMessage('previous_period', lookbackDays),
        'warning',
      );
    } else if (compareMode === 'previous_year' && !canUsePreviousYearCompare) {
      setCompareMode('none');
      setCompareDateRange(null);
      snackbar(
        getLookbackCompareAutoDisabledMessage('previous_year', lookbackDays),
        'warning',
      );
    }
  }, [
    compareMode,
    canUsePreviousPeriodCompare,
    canUsePreviousYearCompare,
    lookbackDays,
    snackbar,
  ]);

  const { data, loading, error } = useQuery<FinanceHistoricalQueryData>(
    GET_FINANCE_HISTORICAL,
    {
      variables: { dateRange, costSettings: activeCostSettings },
      fetchPolicy: 'network-only',
    },
  );

  const compareSkip =
    compareMode === 'none' || !compareDateRange?.from || !compareDateRange?.to;

  const { data: compareData, loading: compareLoading } = useQuery<FinanceHistoricalQueryData>(
    GET_FINANCE_HISTORICAL,
    {
      variables: { dateRange: compareDateRange ?? dateRange, costSettings: activeCostSettings },
      skip: compareSkip,
      fetchPolicy: 'network-only',
    },
  );

  const savedCostSummary = useMemo(
    () => formatSavedCostSummary(parsedSavedCostForm),
    [parsedSavedCostForm],
  );

  const finance = data?.getFinanceHistorical;
  const compareFinance = compareData?.getFinanceHistorical;
  const showCompare = !compareSkip && !!compareFinance?.rows?.length;

  const tableRows = useMemo(() => {
    const rows = finance?.rows ?? [];
    if (!showCompare || !compareFinance?.rows) return rows;
    return attachCompareToFinanceRows(
      rows,
      finance?.periods ?? [],
      compareFinance.rows,
      compareFinance.periods ?? [],
      compareMode,
    );
  }, [finance?.rows, finance?.periods, showCompare, compareFinance, compareMode]);

  const rangeLabel = useMemo(() => {
    if (!finance?.dateRange?.from || !finance?.dateRange?.to) return null;
    return formatRangeLabel(finance.dateRange.from, finance.dateRange.to);
  }, [finance?.dateRange]);

  const compareRangeLabel = useMemo(() => {
    if (!showCompare || !compareFinance?.dateRange?.from || !compareFinance?.dateRange?.to) {
      return null;
    }
    return formatRangeLabel(compareFinance.dateRange.from, compareFinance.dateRange.to);
  }, [showCompare, compareFinance?.dateRange]);

  const applyPreset = (preset: string) => {
    if (preset !== 'custom' && preset !== 'this_month' && preset !== 'this_year') {
      const days = parseInt(preset, 10);
      if (!isNaN(days) && !canUsePresetDays(days)) {
        snackbar(getLookbackPresetBlockedMessage(lookbackDays), 'warning');
        return;
      }
    }
    if (preset === 'this_month' && !canUseThisMonthPreset) {
      snackbar(getLookbackThisMonthBlockedMessage(lookbackDays), 'warning');
      return;
    }
    if (preset === 'this_year' && !canUseThisYearPreset) {
      snackbar(getLookbackThisYearBlockedMessage(lookbackDays), 'warning');
      return;
    }

    setDateRangePreset(preset);
    setShowCustomDateRange(preset === 'custom');
    setTempCustomDates(null);

    if (preset === 'this_month') {
      setDateRange(getThisMonthDateRange());
    } else if (preset === 'this_year') {
      setDateRange(getThisYearDateRange());
    } else if (preset === '180') {
      setDateRange(getLastNMonthsDateRange(6));
    } else if (preset === '365') {
      setDateRange(getLastNMonthsDateRange(12));
    } else if (preset !== 'custom') {
      const days = parseInt(preset, 10);
      if (!isNaN(days)) {
        setDateRange(getLastNDaysDateRange(days));
      }
    } else {
      setTempCustomDates({ from: dateRange.from, to: dateRange.to });
    }
  };

  const handleSaveCostSettings = () => {
    if (costForm.rules.length > 0 && parsedCostForm.rules.length === 0) {
      snackbar('Add a name and amount greater than zero for each cost', 'warning');
      return;
    }
    void updateCostSettings({
      variables: { settings: parsedCostForm },
    });
  };

  const handleClearCostSettings = () => {
    const cleared = emptyFinanceCostSettingsForm();
    setCostForm(cleared);
    void updateCostSettings({
      variables: {
        settings: { rules: [] },
      },
    });
  };

  const canExport =
    !loading &&
    !exportingPdf &&
    Boolean(finance?.periods?.length) &&
    Boolean(tableRows.length) &&
    !finance?.noData &&
    !finance?.error;

  const handleDownloadCsv = useCallback(() => {
    if (!finance?.periods?.length || !tableRows.length || !rangeLabel) {
      snackbar('No finance data available to export.', 'warning');
      return;
    }
    try {
      downloadFinanceCsv({
        periods: finance.periods,
        rows: tableRows,
        granularity: finance.granularity,
        dateRangeLabel: rangeLabel,
        compareActive: showCompare,
      });
      snackbar('Finance CSV downloaded', 'success');
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Could not generate the finance CSV';
      snackbar(message, 'error');
    }
  }, [
    finance?.periods,
    finance?.granularity,
    tableRows,
    rangeLabel,
    showCompare,
    snackbar,
  ]);

  const handleDownloadPdf = useCallback(async () => {
    if (!finance?.periods?.length || !tableRows.length || !rangeLabel) {
      snackbar('No finance data available to export.', 'warning');
      return;
    }
    setExportingPdf(true);
    try {
      await downloadFinancePdf({
        periods: finance.periods,
        rows: tableRows,
        granularity: finance.granularity,
        dateRangeLabel: rangeLabel,
        compareRangeLabel,
        compareActive: showCompare,
        costSummary: savedCostSummary,
      });
      snackbar('Finance PDF downloaded', 'success');
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Could not generate the finance PDF';
      snackbar(message, 'error');
    } finally {
      setExportingPdf(false);
    }
  }, [
    finance?.periods,
    finance?.granularity,
    tableRows,
    rangeLabel,
    compareRangeLabel,
    showCompare,
    savedCostSummary,
    snackbar,
  ]);

  return (
    <PageContainer sx={containerSx}>
      <ContentContainer>
        <Box
          sx={{
            height: 4,
            borderRadius: '0 0 4px 4px',
            background: `linear-gradient(90deg, ${palette.primary.main}, ${palette.primary.light})`,
            mb: 2,
            mx: -0.5,
          }}
        />

        <Box
          sx={{
            mb: 2,
            pb: 2,
            borderBottom: '1px solid',
            borderColor: palette.grey[200],
            background: `linear-gradient(180deg, ${palette.primary.main}05 0%, ${palette.primary.main}02 25%, transparent 70%)`,
            mt: -0.5,
            pt: 2,
            px: 2.5,
            mx: -2.5,
            borderRadius: 2,
          }}
        >
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              px: 1.25,
              py: 0.5,
              borderRadius: '999px',
              bgcolor: `${palette.primary.main}18`,
              border: '1px solid',
              borderColor: `${palette.primary.main}35`,
              mb: 1,
            }}
          >
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: palette.primary.main,
              }}
            />
            <Typography
              sx={{
                color: palette.primary.main,
                fontWeight: 700,
                letterSpacing: 1.1,
                fontSize: '0.7rem',
              }}
            >
              FINANCE
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Box sx={{ minWidth: 0, flex: '1 1 280px' }}>
              <Typography
                variant="h4"
                sx={{
                  color: palette.grey[800],
                  fontWeight: 800,
                  letterSpacing: '-0.025em',
                  lineHeight: 1.2,
                }}
              >
                Store P&amp;L
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 720 }}>
                Historical revenue, margins and marketing efficiency from your connected data
                sources. Columns auto-aggregate by day, month or year based on the selected period.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ flexShrink: 0 }}>
              <Button
                variant="contained"
                startIcon={<FileDownloadOutlinedIcon />}
                disabled={!canExport}
                onClick={handleDownloadCsv}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  mt: { xs: 0.5, sm: 0.25 },
                }}
              >
                Download CSV
              </Button>
              <Button
                variant="outlined"
                startIcon={<FileDownloadOutlinedIcon />}
                disabled={!canExport}
                onClick={() => {
                  void handleDownloadPdf();
                }}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  color: textPrimary,
                  borderColor: 'rgba(15,23,42,0.2)',
                  mt: { xs: 0.5, sm: 0.25 },
                }}
              >
                {exportingPdf ? 'Preparing…' : 'Download PDF'}
              </Button>
            </Stack>
          </Box>
        </Box>

        <Box
          sx={{
            p: 2,
            borderRadius: 2.5,
            border: '1px solid',
            borderColor: 'rgba(15, 23, 42, 0.08)',
            bgcolor: palette.common.white,
            boxShadow: '0 4px 24px -12px rgba(15, 23, 42, 0.1)',
            mb: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-end',
              flexWrap: 'wrap',
              gap: 1.5,
              p: 1.5,
              borderRadius: 2,
              bgcolor: '#f8fafc',
              border: '1px solid rgba(15, 23, 42, 0.06)',
            }}
          >
            <Box sx={{ flexShrink: 0 }}>
              <Typography component="label" sx={periodRowLabelSx}>
                Period
              </Typography>
              <FormControl size="small" sx={{ width: 160, ...periodRowControlSx }}>
                <Select
                  value={dateRangePreset}
                  onChange={(e) => applyPreset(e.target.value)}
                  IconComponent={ExpandMoreIcon}
                  sx={{
                    '& .MuiSelect-select': {
                      py: 0,
                      height: periodRowControlHeight - 2,
                      display: 'flex',
                      alignItems: 'center',
                      fontSize: '0.8125rem',
                      fontWeight: 500,
                    },
                  }}
                >
                  <MenuItem value="30" disabled={!canUsePresetDays(30)}>
                    Last 30 days
                  </MenuItem>
                  <MenuItem value="90" disabled={!canUsePresetDays(90)}>
                    Last 90 days
                  </MenuItem>
                  <MenuItem value="180" disabled={!canUsePresetDays(180)}>
                    Last 6 months
                  </MenuItem>
                  <MenuItem value="365" disabled={!canUsePresetDays(365)}>
                    Last 12 months
                  </MenuItem>
                  <MenuItem value="this_month" disabled={!canUseThisMonthPreset}>
                    This month
                  </MenuItem>
                  <MenuItem value="this_year" disabled={!canUseThisYearPreset}>
                    This year
                  </MenuItem>
                  <MenuItem value="custom">Custom range</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ flexShrink: 0 }}>
              <Typography component="label" sx={periodRowLabelSx}>
                Compare
              </Typography>
              <FormControl size="small" sx={{ width: 155, ...periodRowControlSx }}>
                <Select
                  value={compareMode}
                  onChange={(e) => {
                    const mode = e.target.value as FinanceCompareMode;
                    if (mode === 'previous_period' && !canUsePreviousPeriodCompare) {
                      snackbar(
                        getLookbackCompareBlockedMessage('previous_period', lookbackDays),
                        'warning',
                      );
                      return;
                    }
                    if (mode === 'previous_year' && !canUsePreviousYearCompare) {
                      snackbar(
                        getLookbackCompareBlockedMessage('previous_year', lookbackDays),
                        'warning',
                      );
                      return;
                    }
                    setCompareMode(mode);
                    if (mode === 'none') {
                      setCompareDateRange(null);
                      setShowCustomCompareDates(false);
                      setTempCustomCompareDates(null);
                    } else if (mode === 'previous_period') {
                      setCompareDateRange(getPreviousPeriodDateRange(dateRange));
                      setShowCustomCompareDates(false);
                    } else if (mode === 'previous_year') {
                      setCompareDateRange(getPreviousYearDateRange(dateRange));
                      setShowCustomCompareDates(false);
                    } else if (mode === 'custom') {
                      setShowCustomCompareDates(true);
                      setTempCustomCompareDates(
                        compareDateRange ?? { from: dateRange.from, to: dateRange.to },
                      );
                    }
                  }}
                  IconComponent={ExpandMoreIcon}
                  sx={{
                    '& .MuiSelect-select': {
                      py: 0,
                      height: periodRowControlHeight - 2,
                      display: 'flex',
                      alignItems: 'center',
                      fontSize: '0.8125rem',
                      fontWeight: 500,
                    },
                  }}
                >
                  <MenuItem value="none">None</MenuItem>
                  <MenuItem value="previous_period" disabled={!canUsePreviousPeriodCompare}>
                    Previous period
                  </MenuItem>
                  <MenuItem value="previous_year" disabled={!canUsePreviousYearCompare}>
                    Previous year
                  </MenuItem>
                  <MenuItem value="custom">Custom range</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {showCustomCompareDates && (
              <>
                <TextField
                  type="date"
                  label="Compare from"
                  size="small"
                  value={tempCustomCompareDates?.from ?? compareDateRange?.from ?? ''}
                  onChange={(e) => {
                    const from = e.target.value;
                    const to = tempCustomCompareDates?.to ?? compareDateRange?.to ?? '';
                    setTempCustomCompareDates(from > to ? { from, to: from } : { from, to });
                  }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: lookbackMinDate || undefined }}
                  sx={{ width: 150, ...periodRowControlSx }}
                />
                <TextField
                  type="date"
                  label="Compare to"
                  size="small"
                  value={tempCustomCompareDates?.to ?? compareDateRange?.to ?? ''}
                  onChange={(e) => {
                    const to = e.target.value;
                    const from = tempCustomCompareDates?.from ?? compareDateRange?.from ?? '';
                    setTempCustomCompareDates(to < from ? { from: to, to } : { from, to });
                  }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min: tempCustomCompareDates?.from ?? compareDateRange?.from ?? undefined,
                    max: new Date().toISOString().split('T')[0],
                  }}
                  sx={{ width: 150, ...periodRowControlSx }}
                />
                <Button
                  variant="contained"
                  size="small"
                  className={KIPIFY_FILTER_APPLY_BUTTON_CLASS}
                  onClick={() => {
                    if (
                      tempCustomCompareDates?.from &&
                      tempCustomCompareDates?.to &&
                      tempCustomCompareDates.from <= tempCustomCompareDates.to
                    ) {
                      if (!isRangeWithinLookback(tempCustomCompareDates)) {
                        snackbar(
                          getLookbackCustomRangeBlockedMessage(lookbackDays, 'compare'),
                          'warning',
                        );
                        return;
                      }
                      setCompareDateRange(tempCustomCompareDates);
                    }
                  }}
                  sx={{ minHeight: 40, textTransform: 'none', fontWeight: 600 }}
                >
                  Apply
                </Button>
              </>
            )}

            {showCustomDateRange && (
              <>
                <TextField
                  type="date"
                  label="From"
                  size="small"
                  value={tempCustomDates?.from ?? dateRange.from}
                  onChange={(e) => {
                    const from = e.target.value;
                    const to = tempCustomDates?.to ?? dateRange.to;
                    setTempCustomDates(from > to ? { from, to: from } : { from, to });
                  }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min: lookbackMinDate || undefined,
                    max: tempCustomDates?.to ?? dateRange.to,
                  }}
                  sx={{ width: 150, ...periodRowControlSx }}
                />
                <TextField
                  type="date"
                  label="To"
                  size="small"
                  value={tempCustomDates?.to ?? dateRange.to}
                  onChange={(e) => {
                    const to = e.target.value;
                    const from = tempCustomDates?.from ?? dateRange.from;
                    setTempCustomDates(to < from ? { from: to, to } : { from, to });
                  }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min: tempCustomDates?.from ?? dateRange.from,
                    max: new Date().toISOString().split('T')[0],
                  }}
                  sx={{ width: 150, ...periodRowControlSx }}
                />
                <Button
                  variant="contained"
                  size="small"
                  className={KIPIFY_FILTER_APPLY_BUTTON_CLASS}
                  onClick={() => {
                    if (
                      tempCustomDates?.from &&
                      tempCustomDates?.to &&
                      tempCustomDates.from <= tempCustomDates.to
                    ) {
                      if (!isRangeWithinLookback(tempCustomDates)) {
                        snackbar(
                          getLookbackCustomRangeBlockedMessage(lookbackDays, 'date'),
                          'warning',
                        );
                        return;
                      }
                      setDateRange(tempCustomDates);
                    }
                  }}
                  sx={{ minHeight: 40, textTransform: 'none', fontWeight: 600 }}
                >
                  Apply
                </Button>
              </>
            )}

            <Stack direction="row" spacing={1} sx={{ ml: { md: 'auto' }, alignItems: 'center' }}>
              {finance?.granularity && (
                <Chip
                  icon={<TrendingUpRoundedIcon sx={{ fontSize: '16px !important' }} />}
                  label={granularityLabel(finance.granularity)}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    bgcolor: `${palette.primary.main}12`,
                    color: palette.primary.main,
                    border: '1px solid',
                    borderColor: `${palette.primary.main}30`,
                  }}
                />
              )}
              {rangeLabel && (
                <Typography variant="caption" sx={{ color: textSecondary, fontWeight: 600 }}>
                  {rangeLabel}
                </Typography>
              )}
              {compareRangeLabel && (
                <Typography variant="caption" sx={{ color: textSecondary, fontWeight: 600 }}>
                  vs {compareRangeLabel}
                </Typography>
              )}
            </Stack>
          </Box>

          {lookbackDays !== null && lookbackDays !== undefined && (
            <Typography variant="caption" sx={{ display: 'block', mt: 1.25, color: textSecondary }}>
              {getLookbackPlanLimitCaption(lookbackDays)}
            </Typography>
          )}

          <CustomCostsPanel
            open={costPanelOpen}
            onToggle={() => setCostPanelOpen((open) => !open)}
            form={costForm}
            onChange={setCostForm}
            onSave={handleSaveCostSettings}
            onClear={handleClearCostSettings}
            saving={savingCostSettings}
            dirty={costFormDirty}
            savedSummary={savedCostSummary}
          />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Unable to load finance data. Please try again.
          </Alert>
        )}

        {finance?.error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {finance.error}
          </Alert>
        )}

        {finance?.noData && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No sales or marketing data found for the selected period. Connect Shopify or Magento
            and ad accounts, then try a wider date range (Magento sales must fall inside the
            selected dates).
          </Alert>
        )}

        {finance?.warnings?.length ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {finance.warnings.join(' ')}
          </Alert>
        ) : null}

        <SectionCard
          title="Finance table"
          tooltip="Rows show actual store metrics from Shopify/Magento and ad connectors. Additional costs you configure are classified as marketing or operating and recalculate CM rows automatically."
        >
          {loading || (compareLoading && !compareSkip) ? (
            <Stack spacing={1}>
              <Skeleton variant="rectangular" height={42} sx={{ borderRadius: 1 }} />
              {Array.from({ length: 8 }).map((_, index) => (
                <Skeleton key={index} variant="rectangular" height={34} sx={{ borderRadius: 1 }} />
              ))}
            </Stack>
          ) : (
            <ForecastTable
              periods={finance?.periods ?? []}
              rows={tableRows}
              granularity={finance?.granularity}
              compareActive={showCompare}
            />
          )}
        </SectionCard>
      </ContentContainer>
    </PageContainer>
  );
};

export default FinancePage;
